import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowRight,
  Calendar,
  Clock,
  Headphones,
  MessageSquare,
  Mic,
  Pause,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
  RotateCcw,
  RotateCw,
  Search,
  Volume2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import {
  useAICallAudio,
  useAIAgentCalls,
  type AICall,
} from '@/api/aiAgents'

// The customer's number: the caller on an inbound call, the person called on an outbound one.
const numeroClient = (c: AICall) => (c.direction === 'outbound' ? c.to_number : c.from_number)
// The contact's name when Callizy knows the number, otherwise the number, readable.
const nomAppel = (c: AICall) => c.contact_name || formatPhoneDisplay(numeroClient(c))

interface Props {
  open: boolean
  onClose: () => void
  agentId: number | null
  agentName: string
  companyId: number | undefined
}

interface TranscriptMessage {
  time: string
  role: 'agent' | 'user'
  message: string
}

// On tolère deux formats historiques pour rester compatible avec les appels
// déjà stockés en base :
//   - Format ElevenLabs / Deepgram actuel : `[12s] agent: message`
//   - Ancien format Deepgram : `Agent: message` / `Client: message`
//     (labels FR capitalisés, sans timestamp, tours séparés par lignes vides)
// Labels RESTREINTS aux rôles connus : un pattern générique (`\w+:`) matchait
// les rôles techniques ElevenLabs (ex. `unknown`, jadis filtrés) et n'importe
// quelle ligne `Mot: texte` du corps d'un message, tous mal étiquetés « client ».
const TRANSCRIPT_TIMED_RE = /\[(\d+)s\]\s+(agent|assistant|user|client):\s+(.+)/i
const TRANSCRIPT_UNTIMED_RE = /^(agent|assistant|user|client):\s+(.+)/i

// Normalise n'importe quel libellé de rôle vers 'agent' | 'user'.
function normalizeRole(raw: string): 'agent' | 'user' {
  const r = raw.trim().toLowerCase()
  // Côté agent IA : agent / assistant / Agent (FR)
  if (r === 'agent' || r === 'assistant') return 'agent'
  // Côté client : user / client / Client (FR)
  return 'user'
}

function parseTranscript(transcript: string | null | undefined): TranscriptMessage[] {
  if (!transcript) return []
  const out: TranscriptMessage[] = []
  // On ignore les lignes vides (l'ancien format Deepgram sépare les tours par `\n\n`).
  for (const line of transcript.split('\n')) {
    if (line.trim() === '') continue

    // 1) Format avec timestamp : `[Xs] role: message`
    const timed = TRANSCRIPT_TIMED_RE.exec(line)
    if (timed) {
      out.push({ time: `${timed[1]}s`, role: normalizeRole(timed[2]), message: timed[3] })
      continue
    }

    // 2) Format sans timestamp : `Role: message` (Agent/Client/agent/user)
    const untimed = TRANSCRIPT_UNTIMED_RE.exec(line)
    if (untimed) {
      out.push({ time: '', role: normalizeRole(untimed[1]), message: untimed[2] })
    }
  }
  return out
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function AIAgentCallHistory({
  open,
  onClose,
  agentId,
  agentName,
  companyId,
}: Props) {
  const { t, i18n } = useTranslation()
  const { data: calls = [], isLoading } = useAIAgentCalls(companyId, agentId ?? undefined, open)

  const [selectedCall, setSelectedCall] = useState<AICall | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Auto-select premier appel à l'ouverture.
  useEffect(() => {
    if (open && calls.length > 0 && !selectedCall) {
      setSelectedCall(calls[0])
    }
    if (!open) setSelectedCall(null)
  }, [open, calls, selectedCall])

  // Reset à 0 et play state quand on change d'appel.
  useEffect(() => {
    setIsPlaying(false)
    setCurrentTime(0)
  }, [selectedCall?.id])

  // Audio blob URL (auth via axios) ; révoqué automatiquement au démontage.
  const { data: audioBlobUrl } = useAICallAudio(
    selectedCall?.audio_url ? selectedCall.id : undefined,
  )
  useEffect(() => {
    return () => {
      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl)
    }
  }, [audioBlobUrl])

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-US' : 'fr-FR', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))

  const filtered = calls.filter(
    (c) =>
      numeroClient(c).includes(searchQuery) ||
      c.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.conversation_summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.transcript?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const handlePlayPause = () => {
    if (!audioRef.current || !audioBlobUrl) return
    if (isPlaying) audioRef.current.pause()
    else void audioRef.current.play()
    setIsPlaying(!isPlaying)
  }

  const handleSeek = (delta: number) => {
    if (audioRef.current) audioRef.current.currentTime += delta
  }

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current || !selectedCall) return
    const newTime = (parseFloat(e.target.value) / 100) * (selectedCall.duration || 0)
    audioRef.current.currentTime = newTime
    setCurrentTime(Math.floor(newTime))
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          label: t('manager.aiAgents.callHistory.completed'),
          bg: 'bg-emerald-50 dark:bg-emerald-500/10',
          text: 'text-emerald-700 dark:text-emerald-300',
          border: 'border-emerald-200 dark:border-emerald-500/30',
          dot: 'bg-emerald-500',
        }
      case 'failed':
        return {
          label: t('manager.aiAgents.callHistory.failed'),
          bg: 'bg-red-50 dark:bg-red-500/10',
          text: 'text-red-700 dark:text-red-300',
          border: 'border-red-200 dark:border-red-500/30',
          dot: 'bg-red-500',
        }
      case 'in-progress':
        return {
          label: t('manager.aiAgents.callHistory.inProgress'),
          bg: 'bg-blue-50 dark:bg-blue-500/10',
          text: 'text-blue-700 dark:text-blue-300',
          border: 'border-blue-200 dark:border-blue-500/30',
          dot: 'bg-blue-500',
        }
      default:
        return {
          label: status,
          bg: 'bg-slate-50 dark:bg-slate-800',
          text: 'text-slate-700 dark:text-slate-300',
          border: 'border-slate-200 dark:border-slate-700',
          dot: 'bg-slate-500',
        }
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-4xl p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-4 border-b dark:border-slate-800 bg-gradient-to-r from-slate-50 via-white to-blue-50/40 dark:from-slate-900 dark:via-slate-900 dark:to-blue-500/10">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center shadow-md shadow-blue-600/20">
              <Headphones className="h-4 w-4 text-white" />
            </div>
            <SheetTitle className="text-base font-bold text-slate-900 dark:text-white tracking-tight">
              {agentName}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {t('manager.aiAgents.callHistory.conversationHistory')} {agentName}
            </SheetDescription>
          </div>
        </SheetHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Liste — gauche */}
          <div className="hidden md:flex w-96 border-r border-slate-200 dark:border-slate-800 flex-col bg-white dark:bg-slate-900">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('manager.aiAgents.callHistory.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:bg-white dark:focus:bg-slate-900 transition-colors"
                />
              </div>
            </div>

            <div className="px-4 py-2.5 flex items-center justify-between border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                {t('manager.aiAgents.callHistory.conversations')}
              </span>
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700">
                {filtered.length}
              </span>
            </div>

            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="p-8 text-center">
                  <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                    <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    {t('manager.aiAgents.callHistory.loading')}
                  </div>
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    {t('manager.aiAgents.callHistory.noConversation')}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {t('manager.aiAgents.callHistory.callsAppearHere')}
                  </p>
                </div>
              ) : (
                <div>
                  {filtered.map((call, index) => {
                    const isActive = selectedCall?.id === call.id
                    const cfg = getStatusConfig(call.status)
                    return (
                      <motion.button
                        key={call.id}
                        type="button"
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.3) }}
                        onClick={() => setSelectedCall(call)}
                        className={cn(
                          'w-full text-left p-4 transition-all duration-200 border-b border-slate-100 dark:border-slate-800 last:border-b-0 relative',
                          isActive
                            ? 'bg-blue-50/70 dark:bg-blue-500/10'
                            : 'hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                        )}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="activeCallIndicator"
                            className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-gradient-to-b from-blue-500 to-blue-600"
                            transition={{ type: 'spring', bounce: 0.15, duration: 0.4 }}
                          />
                        )}

                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2.5">
                            <div
                              className={cn(
                                'w-8 h-8 rounded-lg flex items-center justify-center',
                                call.direction === 'inbound'
                                  ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600'
                                  : 'bg-blue-50 dark:bg-blue-500/10 text-blue-600',
                              )}
                            >
                              {call.direction === 'inbound' ? (
                                <PhoneIncoming className="h-4 w-4" />
                              ) : (
                                <PhoneOutgoing className="h-4 w-4" />
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-sm text-slate-800 dark:text-slate-200">
                                {nomAppel(call)}
                              </div>
                              <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                                <Calendar className="h-3 w-3" />
                                {formatDate(call.started_at)}
                              </div>
                            </div>
                          </div>

                          <div
                            className={cn(
                              'flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border',
                              cfg.bg,
                              cfg.text,
                              cfg.border,
                            )}
                          >
                            <div className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
                            {cfg.label}
                          </div>
                        </div>

                        <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-2 ml-[42px]">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDuration(call.duration || 0)}
                          </div>
                          {call.transcript && (
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {parseTranscript(call.transcript).length} msg
                            </div>
                          )}
                        </div>

                        {call.conversation_summary && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 ml-[42px] leading-relaxed">
                            {call.conversation_summary.replace(/\*\*/g, '')}
                          </p>
                        )}
                      </motion.button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Détails — droite */}
          <div className="flex-1 flex flex-col bg-slate-50/30 dark:bg-slate-900/40">
            <AnimatePresence mode="wait">
              {selectedCall ? (
                <motion.div
                  key={selectedCall.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex-1 flex flex-col min-h-0"
                >
                  {/* Header conversation */}
                  <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            'h-10 w-10 rounded-xl flex items-center justify-center shadow-md',
                            selectedCall.direction === 'inbound'
                              ? 'bg-emerald-500 shadow-emerald-500/20'
                              : 'bg-blue-500 shadow-blue-500/20',
                          )}
                        >
                          {selectedCall.direction === 'inbound' ? (
                            <PhoneIncoming className="h-5 w-5 text-white" />
                          ) : (
                            <PhoneOutgoing className="h-5 w-5 text-white" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-slate-900 dark:text-white tracking-tight">
                            {nomAppel(selectedCall)}
                          </h3>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {formatDate(selectedCall.started_at)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-xl px-4 py-2">
                        <Clock className="h-4 w-4 text-slate-500" />
                        <span className="text-xl font-bold text-slate-800 dark:text-slate-200 tabular-nums tracking-tight">
                          {formatDuration(selectedCall.duration || 0)}
                        </span>
                      </div>
                    </div>

                    {/* Audio player */}
                    {selectedCall.audio_url ? (
                      <div className="bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-800 dark:to-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                        {audioBlobUrl && (
                          <audio
                            ref={audioRef}
                            src={audioBlobUrl}
                            onTimeUpdate={() =>
                              audioRef.current &&
                              setCurrentTime(Math.floor(audioRef.current.currentTime))
                            }
                            onEnded={() => setIsPlaying(false)}
                          />
                        )}
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={handlePlayPause}
                            disabled={!audioBlobUrl}
                            className="h-11 w-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shadow-lg shadow-blue-600/25 transition-all duration-200 hover:scale-105 active:scale-95 flex-shrink-0 disabled:opacity-50"
                          >
                            {isPlaying ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4 ml-0.5" />
                            )}
                          </button>

                          <div className="flex-1 min-w-0">
                            <div className="relative h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                                style={{
                                  width: `${selectedCall.duration ? (currentTime / selectedCall.duration) * 100 : 0}%`,
                                }}
                              />
                              <input
                                type="range"
                                min={0}
                                max={100}
                                value={
                                  selectedCall.duration
                                    ? (currentTime / selectedCall.duration) * 100
                                    : 0
                                }
                                onChange={handleProgressChange}
                                className="absolute inset-0 w-full opacity-0 cursor-pointer"
                              />
                            </div>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[11px] text-slate-400 font-mono tabular-nums">
                                {formatDuration(currentTime)}
                              </span>
                              <span className="text-[11px] text-slate-400 font-mono tabular-nums">
                                {formatDuration(selectedCall.duration || 0)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-center gap-1 mt-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 text-[11px] text-slate-500 hover:text-slate-700"
                            onClick={() => handleSeek(-10)}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            10s
                          </Button>
                          <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 mx-1" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 text-[11px] text-slate-500 hover:text-slate-700 font-mono"
                          >
                            1.0x
                          </Button>
                          <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 mx-1" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 text-[11px] text-slate-500 hover:text-slate-700"
                            onClick={() => handleSeek(10)}
                          >
                            <RotateCw className="h-3 w-3 mr-1" />
                            10s
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-50 dark:bg-slate-800 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 p-5 text-center">
                        <div className="h-10 w-10 rounded-xl bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center mx-auto mb-2">
                          <Volume2 className="h-5 w-5 text-slate-400" />
                        </div>
                        <p className="text-xs font-medium text-slate-500">
                          {t('manager.aiAgents.callHistory.noAudio')}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {t('manager.aiAgents.callHistory.audioNotAvailable')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Tabs */}
                  <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0">
                    <TabsList className="w-full justify-start border-b border-slate-200 dark:border-slate-800 rounded-none h-auto p-0 bg-white dark:bg-slate-900 flex-shrink-0">
                      <TabsTrigger
                        value="overview"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:font-semibold text-slate-500 transition-colors px-5 py-3 text-sm"
                      >
                        {t('manager.aiAgents.callHistory.overview')}
                      </TabsTrigger>
                      <TabsTrigger
                        value="transcript"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:font-semibold text-slate-500 transition-colors px-5 py-3 text-sm"
                      >
                        {t('manager.aiAgents.callHistory.transcript')}
                      </TabsTrigger>
                    </TabsList>

                    <div className="flex-1 overflow-hidden min-h-0">
                      <TabsContent
                        value="overview"
                        className="p-6 m-0 h-full overflow-y-auto data-[state=inactive]:hidden"
                      >
                        <div className="space-y-6">
                          <div className="flex items-center gap-6 py-3 px-1 flex-wrap">
                            <div className="flex items-center gap-2.5">
                              <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
                                <MessageSquare className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <div className="text-lg font-bold text-slate-900 dark:text-white leading-none tabular-nums">
                                  {parseTranscript(selectedCall.transcript).length}
                                </div>
                                <div className="text-[11px] text-slate-400 mt-0.5">
                                  {t('manager.aiAgents.callHistory.messages')}
                                </div>
                              </div>
                            </div>

                            <div className="w-px h-8 bg-slate-200 dark:bg-slate-700" />

                            <div className="flex items-center gap-2.5">
                              <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                <Clock className="h-4 w-4 text-slate-500" />
                              </div>
                              <div>
                                <div className="text-lg font-bold text-slate-900 dark:text-white leading-none tabular-nums">
                                  {formatDuration(selectedCall.duration || 0)}
                                </div>
                                <div className="text-[11px] text-slate-400 mt-0.5">
                                  {t('manager.aiAgents.callHistory.duration')}
                                </div>
                              </div>
                            </div>

                            <div className="w-px h-8 bg-slate-200 dark:bg-slate-700" />

                            {(() => {
                              const cfg = getStatusConfig(selectedCall.status)
                              return (
                                <div className="flex items-center gap-2">
                                  <div className={cn('h-2 w-2 rounded-full', cfg.dot)} />
                                  <span className={cn('text-sm font-semibold', cfg.text)}>
                                    {cfg.label}
                                  </span>
                                </div>
                              )
                            })()}
                          </div>

                          {selectedCall.conversation_summary && (
                            <div>
                              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                                {t('manager.aiAgents.callHistory.summary')}
                              </h4>
                              <div className="relative bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm overflow-hidden">
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-400 to-indigo-500 rounded-l-xl" />
                                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line pl-3">
                                  {selectedCall.conversation_summary.replace(/\*\*/g, '')}
                                </p>
                              </div>
                            </div>
                          )}

                          <div>
                            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                              {t('manager.aiAgents.callHistory.callDetails')}
                            </h4>
                            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
                              <DetailRow
                                label={t('manager.aiAgents.callHistory.number')}
                                value={formatPhoneDisplay(numeroClient(selectedCall))}
                              />
                              <div className="flex items-center justify-between px-5 py-3.5">
                                <span className="text-sm text-slate-500">
                                  {t('manager.aiAgents.callHistory.direction')}
                                </span>
                                <div className="flex items-center gap-2">
                                  <div
                                    className={cn(
                                      'h-5 w-5 rounded-md flex items-center justify-center',
                                      selectedCall.direction === 'inbound'
                                        ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600'
                                        : 'bg-blue-50 dark:bg-blue-500/10 text-blue-600',
                                    )}
                                  >
                                    {selectedCall.direction === 'inbound' ? (
                                      <PhoneIncoming className="h-3 w-3" />
                                    ) : (
                                      <PhoneOutgoing className="h-3 w-3" />
                                    )}
                                  </div>
                                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    {selectedCall.direction === 'inbound'
                                      ? t('manager.aiAgents.inbound')
                                      : t('manager.aiAgents.outbound')}
                                  </span>
                                </div>
                              </div>
                              <DetailRow
                                label={t('manager.aiAgents.callHistory.dateTime')}
                                value={formatDate(selectedCall.started_at)}
                              />
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent
                        value="transcript"
                        className="p-0 m-0 h-full flex flex-col data-[state=inactive]:hidden"
                      >
                        {selectedCall.transcript ? (
                          <div className="flex-1 flex flex-col min-h-0">
                            <div className="flex-1 overflow-y-auto p-5 space-y-3 min-h-0 bg-gradient-to-b from-slate-50/50 to-white dark:from-slate-900/50 dark:to-slate-900">
                              {parseTranscript(selectedCall.transcript).map((msg, index) => (
                                <motion.div
                                  key={index}
                                  initial={{ opacity: 0, y: 6 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{
                                    duration: 0.2,
                                    delay: Math.min(index * 0.04, 0.8),
                                  }}
                                  className={cn(
                                    'flex',
                                    msg.role === 'agent' ? 'justify-start' : 'justify-end',
                                  )}
                                >
                                  <div
                                    className={cn(
                                      'max-w-[75%]',
                                      msg.role === 'agent' ? 'order-1' : 'order-2',
                                    )}
                                  >
                                    <div
                                      className={cn(
                                        'flex items-center gap-1.5 mb-1',
                                        msg.role === 'user' && 'flex-row-reverse',
                                      )}
                                    >
                                      <div
                                        className={cn(
                                          'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                                          msg.role === 'agent'
                                            ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                                            : 'bg-slate-300 text-slate-600',
                                        )}
                                      >
                                        {msg.role === 'agent' ? 'IA' : 'C'}
                                      </div>
                                      <span className="text-[10px] text-slate-400 font-medium">
                                        {msg.role === 'agent'
                                          ? t('manager.aiAgents.callHistory.aiAgent')
                                          : t('manager.aiAgents.callHistory.client')}
                                      </span>
                                      <span className="text-[10px] text-slate-300 tabular-nums">
                                        {msg.time}
                                      </span>
                                    </div>
                                    <div
                                      className={cn(
                                        'rounded-2xl px-3.5 py-2.5',
                                        msg.role === 'agent'
                                          ? 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 shadow-sm'
                                          : 'bg-blue-600 text-white shadow-md shadow-blue-600/15',
                                      )}
                                    >
                                      <p className="text-[13px] leading-relaxed">{msg.message}</p>
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>

                            <div className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-3 flex-shrink-0 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Mic className="h-3.5 w-3.5 text-slate-400" />
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                                  {parseTranscript(selectedCall.transcript).length}{' '}
                                  {t('manager.aiAgents.callHistory.messages')}
                                </p>
                              </div>
                              <p className="text-[11px] text-slate-400">
                                {t('manager.aiAgents.callHistory.duration')} :{' '}
                                {formatDuration(selectedCall.duration || 0)}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <EmptyState
                            icon={MessageSquare}
                            title={t('manager.aiAgents.callHistory.noTranscript')}
                            subtitle={t('manager.aiAgents.callHistory.transcriptNotAvailable')}
                          />
                        )}
                      </TabsContent>
                    </div>
                  </Tabs>
                </motion.div>
              ) : (
                <EmptyState
                  icon={ArrowRight}
                  title={t('manager.aiAgents.callHistory.selectConversation')}
                  subtitle={t('manager.aiAgents.callHistory.chooseCallDetails')}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 tabular-nums">
        {value}
      </span>
    </div>
  )
}

function EmptyState({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: typeof Phone
  title: string
  subtitle: string
}) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
          <Icon className="h-7 w-7 text-slate-400" />
        </div>
        <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{title}</p>
        <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
      </div>
    </div>
  )
}
